/**
 * Link Manager - BIM 2025 Grade
 * 
 * Manages external references (links) for the BIM project.
 * Supports:
 * - Link Types: CAD (DWG/DXF), IFC, BIM (RVT), Point Cloud (RCP)
 * - Path Resolution: Absolute vs Relative
 * - Loading States: Loaded, Unloaded, Not Found
 * - Transformation: Shared Coordinates vs Internal Origin
 * - Nested Links handling
 */

import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';

export type LinkType = 'CAD' | 'IFC' | 'BIM' | 'PointCloud' | 'PDF' | 'Image' | 'Topography' | 'Coordination';
export type LinkStatus = 'Loaded' | 'Unloaded' | 'NotFound' | 'Outdated';
export type PathType = 'Absolute' | 'Relative';

export interface LinkInstance {
    id: string;
    name: string;
    type: LinkType;
    path: string;
    pathType: PathType;
    status: LinkStatus;

    // Positioning
    transform: THREE.Matrix4;
    pinned: boolean;

    // Visibility
    visible: boolean;
    worksetId?: string; // For workset control

    // Metadata
    size: number; // bytes
    lastModified: number; // timestamp

    // Loaded Content (Abstract representation)
    documentId?: string; // ID of the loaded content in the engine
}

export class LinkManager {
    private links: Map<string, LinkInstance> = new Map();

    /** Listeners notified on any registry mutation (Manage Links dialog, etc.). */
    private listeners = new Set<() => void>();

    constructor() {
        // Initialize
    }

    /** Subscribe to registry changes; returns an unsubscribe function. */
    public subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private emit(): void {
        this.listeners.forEach(l => { try { l(); } catch { /* listener must not break the registry */ } });
    }

    /**
     * Link an external file
     */
    public linkFile(type: LinkType, path: string, options: {
        positioning: 'OriginToOrigin' | 'CenterToCenter' | 'SharedCoordinates';
        pathType?: PathType;
        documentId?: string; // Loaded content id in the engine (element id)
        size?: number;
    }): LinkInstance {
        const id = uuidv4();
        const name = this.extractFileName(path);

        const instance: LinkInstance = {
            id,
            name,
            type,
            path,
            pathType: options.pathType || 'Relative',
            status: 'Loaded', // Assume loaded initially for now
            transform: new THREE.Matrix4(), // Identity default
            pinned: false,
            visible: true,
            size: options.size ?? 0,
            lastModified: Date.now(),
            documentId: options.documentId
        };

        this.links.set(id, instance);
        console.log(`Linked ${type}: ${name}`);
        this.emit();

        return instance;
    }

    public getLink(id: string): LinkInstance | undefined {
        return this.links.get(id);
    }

    public getAllLinks(): LinkInstance[] {
        return Array.from(this.links.values());
    }

    public getLinksByType(type: LinkType): LinkInstance[] {
        return this.getAllLinks().filter(l => l.type === type);
    }

    /** Bind a link to the wrapper element that renders it in the scene. */
    public setDocumentId(id: string, documentId: string): void {
        const link = this.links.get(id);
        if (!link) return;
        link.documentId = documentId;
        this.emit();
    }

    /** Toggle link visibility (BIM: Manage Links → Visible column). */
    public setVisibility(id: string, visible: boolean): void {
        const link = this.links.get(id);
        if (!link) return;
        link.visible = visible;
        this.emit();
    }

    /** Update load status (Loaded / Unloaded / NotFound / Outdated). */
    public setStatus(id: string, status: LinkStatus): void {
        const link = this.links.get(id);
        if (!link) return;
        link.status = status;
        this.emit();
    }

    public removeLink(id: string): boolean {
        const deleted = this.links.delete(id);
        if (deleted) this.emit();
        return deleted;
    }

    public reloadLink(id: string): void {
        const link = this.links.get(id);
        if (!link) return;

        console.log(`Reloading link: ${link.name}`);
        link.status = 'Loaded';
        link.lastModified = Date.now();
        this.emit();
    }

    public unloadLink(id: string): void {
        const link = this.links.get(id);
        if (!link) return;

        link.status = 'Unloaded';
        link.visible = false;
        this.emit();
    }

    private extractFileName(path: string): string {
        return path.split(/[\\/]/).pop() || path;
    }
}

/**
 * Process-wide registry. Every Insert-tab tool and the Manage Links dialog
 * share this instance, so links created by one are listed by the others.
 */
export const sharedLinkManager = new LinkManager();
